import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  GraduationCap, Gem, Flame, Crown, Lightbulb, Feather, Sparkles, Zap,
  Wand2, Image as ImageIcon, Upload, ArrowRight, ArrowLeft, Check, Mic, X, Loader2, Bell, Tag,
  Palette, ImagePlus, Trash2, Info, Bookmark, Plus, Link2, Gauge,
} from "lucide-react";

const STEP_MOTION = { initial: { opacity: 0, x: 24 }, animate: { opacity: 1, x: 0 }, exit: { opacity: 0, x: -24 }, transition: { duration: 0.3 } };
const SPEED_LABEL = (v) => (v < 0.9 ? "Slow & clear" : v < 0.99 ? "Relaxed" : v <= 1.01 ? "Normal" : v < 1.2 ? "Brisk" : v < 1.4 ? "Fast" : "Rapid-fire");
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import AnimatedCaption from "../components/AnimatedCaption";
import { getConfig, createVideo, uploadImage, mediaUrl, errText, listPresets, createPreset, deletePreset } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const TONE_ICONS = { educational: GraduationCap, modern: Gem, viral: Flame, luxury: Crown, problem_solution: Lightbulb };
const MODEL_ICONS = { claude: Feather, gpt: Sparkles, gemini: Zap };
const VISUAL_ICONS = { ai: Wand2, stock: ImageIcon, upload: Upload };

const SUBJECT_TYPES = [
  { id: "general", label: "General visual reference" },
  { id: "character", label: "Character / person" },
  { id: "product", label: "Product" },
  { id: "object", label: "Object" },
  { id: "environment", label: "Environment / location" },
];

const SelectCard = ({ active, onClick, icon: Icon, title, desc, testid }) => (
  <button
    data-testid={testid}
    onClick={onClick}
    className={`text-left rounded-2xl border p-5 transition-colors ${
      active
        ? "border-[#FF453A] bg-[#FF453A]/10 glow-coral"
        : "border-white/10 bg-[#11141E] hover:border-white/25 hover:bg-[#161A26]"
    }`}
  >
    <div className="flex items-start justify-between">
      <span className={`flex h-11 w-11 items-center justify-center rounded-xl ${active ? "bg-[#FF453A] text-white" : "bg-[#1E2333] text-[#FF6A5E]"}`}>
        <Icon className="h-5 w-5" />
      </span>
      {active && <Check className="h-5 w-5 text-[#FF6A5E]" />}
    </div>
    <h4 className="mt-4 font-display font-bold text-base">{title}</h4>
    <p className="mt-1 text-sm text-slate-400 leading-snug">{desc}</p>
  </button>
);

export default function Studio() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [cfg, setCfg] = useState(null);
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const [product, setProduct] = useState({ name: "", tagline: "", description: "", audience: "", hook: "" });
  const [tone, setTone] = useState("modern");
  const [duration, setDuration] = useState(15);
  const [aspectRatio, setAspectRatio] = useState("9:16");
  const [model, setModel] = useState("gpt");
  const [visual, setVisual] = useState("ai");
  const [voice, setVoice] = useState("nova");
  const [ttsModel, setTtsModel] = useState("tts-1-hd");
  const [narrationSpeed, setNarrationSpeed] = useState(1.0);
  const [uploads, setUploads] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [notifyEmail, setNotifyEmail] = useState("");
  const [aff, setAff] = useState({
    enabled: false, url: "", product_name: "", product_description: "", network: "",
    cta_text: "Shop Now", discount_code: "",
    disclosure_enabled: false,
    disclosure_text: "This video contains affiliate links. I may earn a commission if you purchase through my link.",
    clickable_mode: "cta", tracking_enabled: true,
    cta: { text: "Shop Now", bg_color: "#FF453A", text_color: "#FFFFFF", position: "bottom", size: "md", start_mode: "last5", start_time: 0 },
  });
  const setAffField = (k, v) => setAff((a) => ({ ...a, [k]: v }));
  const setCtaField = (k, v) => setAff((a) => ({ ...a, cta: { ...a.cta, [k]: v } }));
  const [captionsEnabled, setCaptionsEnabled] = useState(true);
  const [captionStyleId, setCaptionStyleId] = useState("bold_pop");
  const [captionsShown, setCaptionsShown] = useState(3);
  const [visualStyleId, setVisualStyleId] = useState("none");
  const [customStyle, setCustomStyle] = useState("");
  const [refs, setRefs] = useState([]); // {path, url, subject_type, description}
  const [refUploading, setRefUploading] = useState(false);

  const stepList = useMemo(() => {
    const s = ["brief", "tone", "model", "visuals"];
    if (visual === "ai") s.push("style", "references");
    s.push("captions");
    return s;
  }, [visual]);
  const cur = stepList[step] || "brief";
  const STEP_LABELS = { brief: "Brief", tone: "Tone", model: "Script AI", visuals: "Visuals & Voice", style: "Visual Style", references: "References", captions: "Captions" };

  useEffect(() => { if (step > stepList.length - 1) setStep(stepList.length - 1); }, [stepList, step]);

  useEffect(() => { getConfig().then((c) => { setCfg(c); if (c.duration_default) setDuration(c.duration_default); }).catch(() => toast.error("Failed to load studio config")); }, []);
  useEffect(() => { listPresets().then(setPresets).catch(() => {}); }, []);

  const applyTemplate = (t) => {
    setProduct({ name: t.name, tagline: t.tagline, description: t.description, audience: t.audience, hook: t.hook });
    setTone(t.tone);
    if (t.narration_speed) setNarrationSpeed(t.narration_speed);
    if (t.affiliate?.enabled) {
      setAff((a) => ({ ...a, ...t.affiliate, enabled: true, cta: { ...a.cta, ...(t.affiliate.cta || {}) } }));
      toast.success(`Loaded "${t.name}" — affiliate link kept`);
    } else {
      toast.success(`Loaded "${t.name}" template`);
    }
  };

  useEffect(() => {
    const id = new URLSearchParams(window.location.search).get("preset");
    if (!id || !presets.length) return;
    const p = presets.find((x) => x.id === id);
    if (p) { applyTemplate(p); window.history.replaceState({}, "", "/studio"); }
  }, [presets]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveCurrentAsPreset = async () => {
    if (!product.name.trim()) { toast.error("Add a product name before saving a preset"); return; }
    setSavingPreset(true);
    try {
      const affiliate = aff.enabled && aff.url ? { ...aff, cta: { ...aff.cta, text: aff.cta.text || aff.cta_text } } : null;
      const p = await createPreset({ ...product, tone, affiliate, narration_speed: narrationSpeed });
      setPresets((list) => [p, ...list]);
      toast.success(`Saved "${p.name}" as a preset${affiliate ? " — affiliate link kept" : ""}`);
    } catch (e) { toast.error(errText(e)); }
    setSavingPreset(false);
  };

  const removePreset = async (e, id) => {
    e.stopPropagation();
    const prev = presets;
    setPresets((list) => list.filter((p) => p.id !== id));
    try { await deletePreset(id); toast.success("Preset removed"); }
    catch { setPresets(prev); toast.error("Failed to remove preset"); }
  };

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      for (const f of files) {
        const res = await uploadImage(f);
        setUploads((u) => [...u, res.path]);
      }
      toast.success("Image uploaded");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  const handleRefUpload = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setRefUploading(true);
    try {
      for (const f of files) {
        const res = await uploadImage(f);
        setRefs((r) => [...r, { path: res.path, url: res.path, subject_type: "general", description: "" }]);
      }
      toast.success("Reference added");
    } catch { toast.error("Upload failed — use jpg, png or webp"); }
    setRefUploading(false);
  };
  const setRefField = (i, k, v) => setRefs((r) => r.map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeRef = (i) => setRefs((r) => r.filter((_, idx) => idx !== i));

  const canNext = useMemo(() => {
    if (cur === "brief") return product.name.trim().length > 1 && product.description.trim().length > 3;
    if (cur === "style") return visualStyleId !== "custom" || customStyle.trim().length > 2;
    return true;
  }, [cur, product, visualStyleId, customStyle]);

  const submit = async () => {
    if (visual === "upload" && uploads.length === 0) {
      toast.error("Upload at least one image or pick another visual source");
      return;
    }
    setSubmitting(true);
    if (aff.enabled && aff.url && !/^https?:\/\/.+/i.test(aff.url.trim())) {
      toast.error("Enter a valid affiliate URL (http/https) or disable affiliate links");
      setSubmitting(false);
      return;
    }
    try {
      const affiliate = { ...aff, cta: { ...aff.cta, text: aff.cta.text || aff.cta_text } };
      const res = await createVideo({
        product, tone, duration, aspect_ratio: aspectRatio, model, visual_source: visual, voice, tts_model: ttsModel, narration_speed: narrationSpeed, upload_paths: uploads,
        notify_email: notifyEmail.trim() || null, app_origin: window.location.origin,
        affiliate,
        captions_enabled: captionsEnabled,
        caption_style_id: captionStyleId,
        visual_style_id: visual === "ai" ? visualStyleId : "none",
        custom_style: visual === "ai" && visualStyleId === "custom" ? customStyle.trim() : "",
        references: visual === "ai" ? refs.map((r) => ({ path: r.path, subject_type: r.subject_type, description: r.description })) : [],
      });
      toast.success("Generation started!");
      refresh();
      navigate(`/video/${res.id}`);
    } catch (e) {
      if (e?.response?.status === 402) {
        toast.error(errText(e));
        navigate("/pricing");
      } else {
        toast.error(errText(e) || "Could not start generation");
      }
      setSubmitting(false);
    }
  };

  if (!cfg) {
    return (
      <div className="flex items-center justify-center py-40 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Loading studio…
      </div>
    );
  }

  const durMin = cfg.duration_min ?? 3;
  const durMax = cfg.duration_max ?? 60;
  const sceneEstimate = duration <= 8 ? 1 : duration <= 14 ? 2 : duration <= 22 ? 3 : duration <= 32 ? 4 : duration <= 44 ? 5 : 6;

  return (
    <div className="relative max-w-5xl mx-auto px-5 sm:px-8 py-12">
      <div className="pointer-events-none absolute -top-20 right-0 h-80 w-80 rounded-full bg-[#FF453A]/12 blur-[120px]" />

      <div className="mb-2"><span className="eyebrow">Video creator</span></div>
      <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight">Build your ad</h1>

      {/* stepper */}
      <div className="mt-8 flex items-center gap-2 sm:gap-4">
        {stepList.map((s, i) => (
          <div key={s} className="flex items-center gap-2 sm:gap-4 flex-1">
            <button
              data-testid={`step-tab-${i}`}
              onClick={() => i < step && setStep(i)}
              className="flex items-center gap-2.5"
            >
              <span className={`flex h-8 w-8 items-center justify-center rounded-full text-sm font-bold transition-colors ${
                i === step ? "bg-[#FF453A] text-white" : i < step ? "bg-[#FF453A]/20 text-[#FF6A5E]" : "bg-[#1E2333] text-slate-500"
              }`}>
                {i < step ? <Check className="h-4 w-4" /> : i + 1}
              </span>
              <span className={`hidden sm:block text-sm font-medium ${i === step ? "text-white" : "text-slate-500"}`}>{STEP_LABELS[s]}</span>
            </button>
            {i < stepList.length - 1 && <span className={`h-px flex-1 ${i < step ? "bg-[#FF453A]/40" : "bg-white/10"}`} />}
          </div>
        ))}
      </div>

      <div className="mt-10">
        <AnimatePresence mode="wait">
          <motion.div
            key={step}
            initial={STEP_MOTION.initial}
            animate={STEP_MOTION.animate}
            exit={STEP_MOTION.exit}
            transition={STEP_MOTION.transition}
          >
            {/* STEP — brief */}
            {cur === "brief" && (
              <div className="space-y-6">
                <div className="flex flex-wrap gap-2">
                  <span className="text-xs text-slate-500 self-center mr-1">Quick start:</span>
                  {cfg.templates.map((t) => (
                    <button
                      key={t.name}
                      data-testid={`template-${t.name.split(" ")[0].toLowerCase()}`}
                      onClick={() => applyTemplate(t)}
                      className="rounded-full border border-white/10 bg-[#11141E] px-3.5 py-1.5 text-xs font-medium text-slate-300 hover:border-[#FF453A]/50 hover:text-white transition-colors"
                    >
                      {t.name}
                    </button>
                  ))}
                </div>

                {presets.length > 0 && (
                  <div className="flex flex-wrap gap-2" data-testid="custom-presets-row">
                    <span className="text-xs text-slate-500 self-center mr-1 inline-flex items-center gap-1">
                      <Bookmark className="h-3 w-3" /> Your presets:
                    </span>
                    {presets.map((p) => (
                      <span
                        key={p.id}
                        data-testid={`preset-chip-${p.id}`}
                        className="group inline-flex items-center rounded-full border border-[#FF453A]/30 bg-[#FF453A]/10 pl-3.5 pr-1.5 py-1.5 text-xs font-medium text-slate-200 hover:border-[#FF453A]/60 transition-colors"
                      >
                        <button data-testid={`preset-apply-${p.id}`} onClick={() => applyTemplate(p)} className="hover:text-white inline-flex items-center gap-1.5">
                          {p.name}
                          {p.affiliate?.enabled && <Link2 className="h-3 w-3 text-[#FF8A80]" aria-label="Includes affiliate link" />}
                        </button>
                        <button
                          data-testid={`preset-delete-${p.id}`}
                          onClick={(e) => removePreset(e, p.id)}
                          aria-label={`Delete preset ${p.name}`}
                          className="ml-1.5 flex h-4 w-4 items-center justify-center rounded-full text-slate-400 hover:bg-[#FF453A]/30 hover:text-white transition-colors"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-300">Product / service name *</label>
                    <Input data-testid="input-name" value={product.name} onChange={(e) => setProduct({ ...product, name: e.target.value })} placeholder="HydraPod Smart Bottle" className="mt-2 bg-[#11141E] border-white/10 h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300">Tagline</label>
                    <Input data-testid="input-tagline" value={product.tagline} onChange={(e) => setProduct({ ...product, tagline: e.target.value })} placeholder="Hydration, reinvented" className="mt-2 bg-[#11141E] border-white/10 h-11" />
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-slate-300">What is it? Describe it *</label>
                  <Textarea data-testid="input-description" value={product.description} onChange={(e) => setProduct({ ...product, description: e.target.value })} placeholder="A smart water bottle that tracks your intake and glows to remind you to drink…" className="mt-2 bg-[#11141E] border-white/10 min-h-[110px]" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="text-sm font-medium text-slate-300">Target audience</label>
                    <Input data-testid="input-audience" value={product.audience} onChange={(e) => setProduct({ ...product, audience: e.target.value })} placeholder="Busy professionals & gym-goers" className="mt-2 bg-[#11141E] border-white/10 h-11" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-300">Key hook / angle</label>
                    <Input data-testid="input-hook" value={product.hook} onChange={(e) => setProduct({ ...product, hook: e.target.value })} placeholder="You're dehydrated right now" className="mt-2 bg-[#11141E] border-white/10 h-11" />
                  </div>
                </div>
                <div className="flex justify-end">
                  <Button
                    data-testid="save-preset-btn"
                    type="button"
                    variant="outline"
                    disabled={savingPreset || !product.name.trim()}
                    onClick={saveCurrentAsPreset}
                    className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-[#161A26] hover:text-white h-10 disabled:opacity-50"
                  >
                    {savingPreset ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1.5" /> Save as preset</>}
                  </Button>
                </div>
              </div>
            )}

            {/* STEP — tone + duration */}
            {cur === "tone" && (
              <div className="space-y-8">
                <div>
                  <h3 className="font-display font-bold text-lg mb-4">Pick your tone</h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cfg.tones.map((t) => (
                      <SelectCard key={t.id} testid={`tone-${t.id}`} active={tone === t.id} onClick={() => setTone(t.id)} icon={TONE_ICONS[t.id] || Gem} title={t.name} desc={t.desc} />
                    ))}
                  </div>
                </div>
                <div>
                  <h3 className="font-display font-bold text-lg mb-1">Video duration</h3>
                  <p className="text-sm text-slate-400 mb-6">Choose the length of your video. The AI matches the script, voiceover, footage and captions to it.</p>

                  <div className="rounded-2xl border border-white/10 bg-[#11141E] p-6">
                    <div className="flex items-end justify-between mb-5">
                      <div>
                        <p className="eyebrow">Selected video length</p>
                        <p className="mt-1 font-display font-extrabold text-4xl text-white leading-none" data-testid="duration-value">
                          {duration}<span className="text-xl text-[#FF6A5E] ml-1">sec</span>
                        </p>
                      </div>
                      <span className="text-xs text-slate-500 font-code">≈ {sceneEstimate} scene{sceneEstimate > 1 ? "s" : ""}</span>
                    </div>
                    <Slider
                      data-testid="duration-slider"
                      value={[duration]}
                      min={durMin}
                      max={durMax}
                      step={1}
                      onValueChange={(v) => setDuration(v[0])}
                      className="my-2"
                    />
                    <div className="mt-3 flex justify-between text-xs text-slate-500 font-code">
                      <span>{durMin}s</span>
                      <span>{Math.round((durMin + durMax) / 2)}s</span>
                      <span>{durMax}s</span>
                    </div>
                    <div className="mt-5 flex flex-wrap gap-2">
                      {[3, 15, 30, 45, 60].filter((d) => d >= durMin && d <= durMax).map((d) => (
                        <button
                          key={d}
                          data-testid={`duration-preset-${d}`}
                          onClick={() => setDuration(d)}
                          className={`rounded-full px-4 py-1.5 text-sm font-medium transition-colors ${
                            duration === d ? "bg-[#FF453A] text-white" : "bg-[#1E2333] text-slate-300 hover:bg-[#262c3d]"
                          }`}
                        >
                          {d}s
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mt-8">
                  <h3 className="font-display font-bold text-lg mb-1">Aspect ratio</h3>
                  <p className="text-sm text-slate-400 mb-4">Pick the shape that fits where you'll post.</p>
                  <div className="grid grid-cols-3 gap-3">
                    {(cfg.aspect_ratios || []).map((a) => {
                      const active = aspectRatio === a.id;
                      const box = a.id === "9:16" ? "w-6 h-10" : a.id === "1:1" ? "w-8 h-8" : "w-10 h-6";
                      return (
                        <button key={a.id} type="button" data-testid={`aspect-${a.id.replace(":", "-")}`}
                          onClick={() => setAspectRatio(a.id)}
                          className={`rounded-2xl border-2 p-4 flex flex-col items-center gap-2 transition-all ${active ? "border-[#FF453A] bg-[#FF453A]/10" : "border-white/10 bg-[#11141E] hover:border-white/25"}`}>
                          <span className={`${box} rounded-md border-2 ${active ? "border-[#FF6A5E]" : "border-slate-500"}`} />
                          <span className="text-sm font-semibold">{a.name}</span>
                          <span className="text-[11px] text-slate-400 text-center leading-tight">{a.desc}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

            {/* STEP — model */}
            {cur === "model" && (
              <div>
                <h3 className="font-display font-bold text-lg mb-4">Choose the script AI</h3>
                <div className="grid sm:grid-cols-3 gap-4">
                  {cfg.models.map((m) => (
                    <SelectCard key={m.id} testid={`model-${m.id}`} active={model === m.id} onClick={() => setModel(m.id)} icon={MODEL_ICONS[m.id] || Sparkles} title={m.name} desc={m.desc} />
                  ))}
                </div>
              </div>
            )}

            {/* STEP — visuals + voice */}
            {cur === "visuals" && (
              <div className="space-y-8">
                <div>
                  <h3 className="font-display font-bold text-lg mb-4">Visual source</h3>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {cfg.visual_sources.map((v) => (
                      <SelectCard key={v.id} testid={`visual-${v.id}`} active={visual === v.id} onClick={() => setVisual(v.id)} icon={VISUAL_ICONS[v.id] || Wand2} title={v.name} desc={v.desc} />
                    ))}
                  </div>
                  {visual === "upload" && (
                    <div className="mt-4 rounded-2xl border border-dashed border-white/20 bg-[#11141E] p-5">
                      <label data-testid="upload-dropzone" className="flex flex-col items-center justify-center cursor-pointer py-4">
                        <Upload className="h-6 w-6 text-[#FF6A5E]" />
                        <span className="mt-2 text-sm text-slate-300">{uploading ? "Uploading…" : "Click to upload images (jpg, png, webp)"}</span>
                        <input data-testid="upload-input" type="file" accept="image/*" multiple className="hidden" onChange={handleUpload} />
                      </label>
                      {uploads.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-3">
                          {uploads.map((p, i) => (
                            <div key={p} className="relative h-20 w-20 rounded-lg overflow-hidden border border-white/10">
                              <img src={mediaUrl(p)} alt="" className="h-full w-full object-cover" />
                              <button data-testid={`remove-upload-${i}`} onClick={() => setUploads(uploads.filter((x) => x !== p))} className="absolute top-1 right-1 h-5 w-5 rounded-full bg-black/70 flex items-center justify-center">
                                <X className="h-3 w-3 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="grid sm:grid-cols-2 gap-6">
                  <div>
                    <h3 className="font-display font-bold text-lg mb-4 flex items-center gap-2"><Mic className="h-5 w-5 text-[#FF6A5E]" /> Voiceover</h3>
                    <Select value={voice} onValueChange={setVoice}>
                      <SelectTrigger data-testid="voice-select" className="bg-[#11141E] border-white/10 h-11"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#11141E] border-white/10">
                        {cfg.voices.map((v) => (
                          <SelectItem key={v.id} value={v.id} data-testid={`voice-${v.id}`}>{v.name} — {v.desc}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-lg mb-4">Voice quality</h3>
                    <div className="flex gap-3">
                      {[{ id: "tts-1", n: "Standard" }, { id: "tts-1-hd", n: "HD" }].map((q) => (
                        <button
                          key={q.id}
                          data-testid={`tts-${q.id}`}
                          onClick={() => setTtsModel(q.id)}
                          className={`flex-1 rounded-xl px-4 py-3 font-medium transition-colors ${ttsModel === q.id ? "bg-[#FF453A] text-white" : "bg-[#11141E] border border-white/10 text-slate-300 hover:border-white/30"}`}
                        >
                          {q.n}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
                <div className="mt-6 rounded-2xl border border-white/10 bg-[#11141E] p-5" data-testid="narration-speed-card">
                  <div className="flex items-end justify-between gap-4">
                    <div>
                      <h3 className="font-display font-bold text-base flex items-center gap-2">
                        <Gauge className="h-4 w-4 text-[#FF6A5E]" /> Narration speed
                      </h3>
                      <p className="mt-1 text-sm text-slate-400">Pitch stays natural — only the pace changes. Normal is the starting point.</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="font-display font-extrabold text-3xl text-white leading-none" data-testid="narration-speed-value">
                        {narrationSpeed.toFixed(2).replace(/\.?0+$/, "")}<span className="text-lg text-[#FF6A5E]">×</span>
                      </p>
                      <p className="text-xs text-slate-500 font-code mt-1">{SPEED_LABEL(narrationSpeed)}</p>
                    </div>
                  </div>
                  <Slider
                    data-testid="narration-speed-slider"
                    value={[narrationSpeed]}
                    min={0.75}
                    max={1.5}
                    step={0.05}
                    onValueChange={(v) => setNarrationSpeed(Math.round(v[0] * 100) / 100)}
                    className="my-4"
                  />
                  <div className="flex justify-between text-xs text-slate-500 font-code">
                    <span>0.75× slower</span>
                    <button data-testid="narration-speed-reset" onClick={() => setNarrationSpeed(1.0)} className="hover:text-white transition-colors">1× normal</button>
                    <span>1.5× faster</span>
                  </div>
                </div>
                <div className="mt-6 rounded-2xl border border-white/10 bg-[#11141E] p-5">
                  <h3 className="font-display font-bold text-base flex items-center gap-2">
                    <Bell className="h-4 w-4 text-[#FF6A5E]" /> Email me when it's ready <span className="text-xs font-normal text-slate-500">(optional)</span>
                  </h3>
                  <p className="mt-1 text-sm text-slate-400">Rendering runs in the background — drop your email and we'll ping you with a link the moment it's done.</p>
                  <Input
                    data-testid="notify-email-input"
                    type="email"
                    value={notifyEmail}
                    onChange={(e) => setNotifyEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="mt-3 bg-[#0B0C10] border-white/10 h-11"
                  />
                </div>

                <div className="mt-6 rounded-2xl border border-white/10 bg-[#11141E] p-5">
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-base flex items-center gap-2">
                      <Tag className="h-4 w-4 text-[#FF6A5E]" /> Affiliate Link &amp; Product Promotion
                    </h3>
                    <button
                      type="button"
                      data-testid="affiliate-toggle"
                      onClick={() => setAffField("enabled", !aff.enabled)}
                      className={`relative h-6 w-11 rounded-full transition-colors ${aff.enabled ? "bg-[#FF453A]" : "bg-[#2a3145]"}`}
                    >
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${aff.enabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                  </div>
                  <p className="mt-1 text-sm text-slate-400">Promote a product and make your video clickable to your affiliate destination. Your tracking parameters are preserved exactly.</p>

                  {aff.enabled && (
                    <div className="mt-5 space-y-4" data-testid="affiliate-fields">
                      <div className="grid sm:grid-cols-2 gap-3">
                        <div className="sm:col-span-2">
                          <label className="text-xs text-slate-400">Affiliate link URL *</label>
                          <Input data-testid="affiliate-url" value={aff.url} onChange={(e) => setAffField("url", e.target.value)} placeholder="https://example.com/product?affiliate=123" className="mt-1 bg-[#0B0C10] border-white/10 h-11" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Product name</label>
                          <Input data-testid="affiliate-product" value={aff.product_name} onChange={(e) => setAffField("product_name", e.target.value)} placeholder="Dog Grooming Brush" className="mt-1 bg-[#0B0C10] border-white/10 h-11" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Affiliate program / network</label>
                          <Input data-testid="affiliate-network" value={aff.network} onChange={(e) => setAffField("network", e.target.value)} placeholder="e.g. Amazon Associates" className="mt-1 bg-[#0B0C10] border-white/10 h-11" />
                        </div>
                        <div className="sm:col-span-2">
                          <label className="text-xs text-slate-400">Product description <span className="text-slate-600">(the AI only uses facts you provide)</span></label>
                          <Textarea data-testid="affiliate-desc" value={aff.product_description} onChange={(e) => setAffField("product_description", e.target.value)} placeholder="Self-cleaning slicker brush that removes loose fur." className="mt-1 bg-[#0B0C10] border-white/10 min-h-[70px]" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Call-to-action text</label>
                          <Input data-testid="affiliate-cta-text" value={aff.cta.text} onChange={(e) => { setCtaField("text", e.target.value); setAffField("cta_text", e.target.value); }} placeholder="Shop Now" className="mt-1 bg-[#0B0C10] border-white/10 h-11" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Discount code <span className="text-slate-600">(optional)</span></label>
                          <Input data-testid="affiliate-discount" value={aff.discount_code} onChange={(e) => setAffField("discount_code", e.target.value)} placeholder="SAVE10" className="mt-1 bg-[#0B0C10] border-white/10 h-11" />
                        </div>
                      </div>

                      <div>
                        <label className="text-xs text-slate-400">Clickable video</label>
                        <div className="mt-2 grid grid-cols-2 gap-2">
                          {[["entire", "Entire video"], ["cta", "CTA button only"], ["area", "Product tag area"], ["none", "No clickable link"]].map(([id, label]) => (
                            <button key={id} type="button" data-testid={`clickable-${id}`} onClick={() => setAffField("clickable_mode", id)}
                              className={`rounded-xl px-3 py-2.5 text-sm font-medium text-left transition-colors ${aff.clickable_mode === id ? "bg-[#FF453A] text-white" : "bg-[#0B0C10] border border-white/10 text-slate-300 hover:border-white/30"}`}>
                              {label}
                            </button>
                          ))}
                        </div>
                      </div>

                      <div className="grid sm:grid-cols-4 gap-3 items-end">
                        <div>
                          <label className="text-xs text-slate-400">Button color</label>
                          <input type="color" data-testid="cta-bg" value={aff.cta.bg_color} onChange={(e) => setCtaField("bg_color", e.target.value)} className="mt-1 h-11 w-full rounded-lg bg-[#0B0C10] border border-white/10 cursor-pointer" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Text color</label>
                          <input type="color" data-testid="cta-fg" value={aff.cta.text_color} onChange={(e) => setCtaField("text_color", e.target.value)} className="mt-1 h-11 w-full rounded-lg bg-[#0B0C10] border border-white/10 cursor-pointer" />
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Position</label>
                          <Select value={aff.cta.position} onValueChange={(v) => setCtaField("position", v)}>
                            <SelectTrigger data-testid="cta-position" className="mt-1 bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#11141E] border-white/10">
                              <SelectItem value="top">Top</SelectItem><SelectItem value="center">Center</SelectItem><SelectItem value="bottom">Bottom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div>
                          <label className="text-xs text-slate-400">Timing</label>
                          <Select value={aff.cta.start_mode} onValueChange={(v) => setCtaField("start_mode", v)}>
                            <SelectTrigger data-testid="cta-timing" className="mt-1 bg-[#0B0C10] border-white/10 h-11"><SelectValue /></SelectTrigger>
                            <SelectContent className="bg-[#11141E] border-white/10">
                              <SelectItem value="always">Whole video</SelectItem><SelectItem value="last5">Final 5 seconds</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="flex items-start gap-3 rounded-xl bg-[#0B0C10] border border-white/10 p-3">
                        <button type="button" data-testid="disclosure-toggle" onClick={() => setAffField("disclosure_enabled", !aff.disclosure_enabled)}
                          className={`mt-0.5 relative h-6 w-11 shrink-0 rounded-full transition-colors ${aff.disclosure_enabled ? "bg-[#FF453A]" : "bg-[#2a3145]"}`}>
                          <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${aff.disclosure_enabled ? "left-[22px]" : "left-0.5"}`} />
                        </button>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-white">Affiliate disclosure</p>
                          {aff.disclosure_enabled && (
                            <Textarea data-testid="disclosure-text" value={aff.disclosure_text} onChange={(e) => setAffField("disclosure_text", e.target.value)} className="mt-2 bg-[#11141E] border-white/10 min-h-[54px] text-sm" />
                          )}
                        </div>
                      </div>

                      <div className="rounded-xl border border-amber-500/25 bg-amber-500/10 p-3">
                        <p className="text-xs text-amber-200/90 leading-relaxed">
                          On the Faceless Ad Studio page your video &amp; button are fully clickable. A downloaded MP4 is <strong>not</strong> universally clickable on social platforms — use their bio link / shopping tag / link sticker (export tools are provided after generation).
                        </p>
                      </div>
                    </div>
                  )}
                </div>


              </div>
            )}

            {/* STEP — visual style (AI only) */}
            {cur === "style" && (
              <div data-testid="visual-style-step">
                <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><Palette className="h-5 w-5 text-[#FF6A5E]" /> Visual style</h3>
                <p className="text-sm text-slate-400 mb-5">Pick one look. It's applied consistently across every scene — backgrounds, characters, objects, lighting and colors. Optional; "No Specific Style" lets the AI choose.</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                  {(cfg.visual_styles || []).map((s) => {
                    const active = visualStyleId === s.id;
                    return (
                      <button key={s.id} type="button" data-testid={`style-card-${s.id}`}
                        onClick={() => setVisualStyleId(s.id)}
                        className={`relative text-left rounded-2xl overflow-hidden border-2 transition-all ${active ? "border-[#FF453A]" : "border-white/10 hover:border-white/25"}`}>
                        <div className="relative h-32 bg-gradient-to-br from-[#1b2030] to-[#0c0e16] flex items-center justify-center">
                          {s.preview ? (
                            <img src={s.preview} alt={s.name} className="h-full w-full object-cover" loading="lazy" />
                          ) : (
                            <span className="flex flex-col items-center text-slate-400">
                              {s.id === "custom" ? <Wand2 className="h-7 w-7" /> : <Sparkles className="h-7 w-7" />}
                            </span>
                          )}
                          {active && (
                            <span data-testid={`style-selected-${s.id}`} className="absolute top-2 right-2 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF453A]">
                              <Check className="h-3.5 w-3.5 text-white" />
                            </span>
                          )}
                        </div>
                        <div className="px-3 py-2 bg-[#11141E]">
                          <p className="text-sm font-semibold leading-tight">{s.name}</p>
                          <p className="mt-0.5 text-[11px] text-slate-400 leading-snug">{s.desc}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
                {visualStyleId === "custom" && (
                  <div className="mt-5">
                    <label className="text-sm font-medium text-slate-300">Describe your custom style *</label>
                    <Textarea data-testid="custom-style-input" value={customStyle} onChange={(e) => setCustomStyle(e.target.value)}
                      placeholder="e.g. vaporwave neon retro, chrome textures, 80s sunset gradients, dreamy glow"
                      className="mt-2 bg-[#11141E] border-white/10 min-h-[80px]" />
                  </div>
                )}
              </div>
            )}

            {/* STEP — image references (AI only) */}
            {cur === "references" && (
              <div data-testid="references-step">
                <h3 className="font-display font-bold text-lg mb-1 flex items-center gap-2"><ImagePlus className="h-5 w-5 text-[#FF6A5E]" /> Upload image references <span className="text-xs font-normal text-slate-500">(optional)</span></h3>
                <p className="text-sm text-slate-400 mb-5">Add photos of people, products, objects or locations. The AI keeps them recognizable while re-rendering them in your chosen style.</p>

                <div
                  data-testid="ref-dropzone"
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={(e) => { e.preventDefault(); handleRefUpload(e.dataTransfer.files); }}
                  className="rounded-2xl border border-dashed border-white/20 bg-[#11141E] p-6"
                >
                  <label className="flex flex-col items-center justify-center cursor-pointer py-2 text-center">
                    <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-[#1E2333]">
                      {refUploading ? <Loader2 className="h-5 w-5 text-[#FF6A5E] animate-spin" /> : <Upload className="h-5 w-5 text-[#FF6A5E]" />}
                    </span>
                    <span className="mt-3 text-sm text-slate-300 font-medium">{refUploading ? "Uploading…" : "Drag & drop or click to upload"}</span>
                    <span className="mt-1 text-xs text-slate-500">JPG, PNG or WEBP · multiple allowed</span>
                    <input data-testid="ref-upload-input" type="file" accept="image/jpeg,image/jpg,image/png,image/webp" multiple className="hidden" onChange={(e) => handleRefUpload(e.target.files)} />
                  </label>
                </div>

                {refs.length > 0 && (
                  <div className="mt-5 space-y-3">
                    {refs.length > 4 && (
                      <p data-testid="ref-cap-note" className="text-xs text-amber-300/90">Heads up: the first 4 references are used to guide generation.</p>
                    )}
                    {refs.map((r, i) => (
                      <div key={r.path} data-testid={`ref-item-${i}`} className="flex gap-4 rounded-2xl border border-white/10 bg-[#11141E] p-3">
                        <div className="relative h-24 w-24 shrink-0 rounded-xl overflow-hidden border border-white/10">
                          <img src={mediaUrl(r.url)} alt="" className="h-full w-full object-cover" />
                        </div>
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="grid sm:grid-cols-2 gap-2">
                            <div>
                              <label className="text-[11px] text-slate-400">Use as</label>
                              <Select value={r.subject_type} onValueChange={(v) => setRefField(i, "subject_type", v)}>
                                <SelectTrigger data-testid={`ref-subject-${i}`} className="mt-1 bg-[#0B0C10] border-white/10 h-9 text-sm"><SelectValue /></SelectTrigger>
                                <SelectContent className="bg-[#11141E] border-white/10">
                                  {SUBJECT_TYPES.map((st) => <SelectItem key={st.id} value={st.id}>{st.label}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            </div>
                            <div>
                              <label className="text-[11px] text-slate-400">Description (optional)</label>
                              <Input data-testid={`ref-desc-${i}`} value={r.description} onChange={(e) => setRefField(i, "description", e.target.value)} placeholder="e.g. red sneaker, brand logo" className="mt-1 bg-[#0B0C10] border-white/10 h-9 text-sm" />
                            </div>
                          </div>
                        </div>
                        <button data-testid={`ref-remove-${i}`} onClick={() => removeRef(i)} className="self-start h-8 w-8 shrink-0 rounded-lg bg-[#1E2333] flex items-center justify-center text-slate-400 hover:text-red-400 transition-colors">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="mt-5 flex items-start gap-2 rounded-xl border border-white/10 bg-[#0B0C10] p-3">
                  <Info className="h-4 w-4 text-slate-500 mt-0.5 shrink-0" />
                  <p className="text-xs text-slate-400 leading-relaxed">The AI uses your images as a guide, not an exact copy. It aims to preserve key features but can't guarantee identical faces or product details.</p>
                </div>
              </div>
            )}

            {cur === "captions" && (
              <div data-testid="captions-step">
                <h3 className="font-display font-bold text-lg mb-1">Animated captions</h3>
                <p className="text-sm text-slate-400 mb-5">Pick one caption style — previews are live and free. You can also turn captions off.</p>

                {visual === "ai" && (
                  <div data-testid="review-summary" className="mb-6 rounded-2xl border border-[#FF453A]/25 bg-[#FF453A]/[0.06] p-4">
                    <p className="eyebrow mb-2">Review — visual setup</p>
                    <div className="flex flex-wrap items-center gap-3 text-sm">
                      <span className="inline-flex items-center gap-1.5 text-slate-300"><Palette className="h-4 w-4 text-[#FF6A5E]" /> Style: <strong className="text-white">{(cfg.visual_styles || []).find((s) => s.id === visualStyleId)?.name || "None"}</strong>{visualStyleId === "custom" && customStyle ? ` — "${customStyle.slice(0, 40)}"` : ""}</span>
                      <span className="inline-flex items-center gap-1.5 text-slate-300"><ImagePlus className="h-4 w-4 text-[#FF6A5E]" /> {refs.length} reference{refs.length === 1 ? "" : "s"}</span>
                    </div>
                    {refs.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {refs.map((r, i) => (
                          <div key={r.path} data-testid={`review-ref-${i}`} className="h-12 w-12 rounded-lg overflow-hidden border border-white/10">
                            <img src={mediaUrl(r.url)} alt="" className="h-full w-full object-cover" />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div className="flex items-center justify-between rounded-2xl border border-white/10 bg-[#11141E] p-4 mb-5">
                  <div className="flex items-center gap-3">
                    <button type="button" data-testid="captions-enabled-toggle"
                      onClick={() => setCaptionsEnabled((v) => !v)}
                      className={`relative h-6 w-11 rounded-full transition-colors ${captionsEnabled ? "bg-[#FF453A]" : "bg-[#2a3145]"}`}>
                      <span className={`absolute top-0.5 h-5 w-5 rounded-full bg-white transition-all ${captionsEnabled ? "left-[22px]" : "left-0.5"}`} />
                    </button>
                    <span className="text-sm font-medium">{captionsEnabled ? "Captions on" : "No captions"}</span>
                  </div>
                  <span data-testid="captions-counter" className="text-xs text-slate-500 font-code">
                    {Math.min(captionsShown, (cfg.caption_styles || []).length)} of {(cfg.caption_styles || []).length} styles
                  </span>
                </div>

                {captionsEnabled && (
                  <>
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      {(cfg.caption_styles || []).slice(0, captionsShown).map((s) => {
                        const active = captionStyleId === s.id;
                        return (
                          <button key={s.id} type="button" data-testid={`caption-card-${s.id}`}
                            onClick={() => setCaptionStyleId(s.id)}
                            className={`relative text-left rounded-2xl overflow-hidden border-2 transition-all ${active ? "border-[#FF453A]" : "border-white/10 hover:border-white/25"}`}>
                            <div className="relative h-40 bg-gradient-to-br from-[#1b2030] to-[#0c0e16]">
                              <AnimatedCaption style={s} text="Your caption here" loop />
                              {active && (
                                <span data-testid={`caption-selected-${s.id}`} className="absolute top-2 right-2 z-20 flex h-6 w-6 items-center justify-center rounded-full bg-[#FF453A]">
                                  <Check className="h-3.5 w-3.5 text-white" />
                                </span>
                              )}
                            </div>
                            <div className="flex items-center justify-between px-3 py-2 bg-[#11141E]">
                              <span className="text-sm font-semibold">{s.name}</span>
                              <span className={`text-[11px] font-semibold ${active ? "text-[#FF6A5E]" : "text-slate-500"}`}>{active ? "Selected" : "Select"}</span>
                            </div>
                          </button>
                        );
                      })}
                    </div>
                    {captionsShown < (cfg.caption_styles || []).length && (
                      <div className="mt-4 flex justify-center">
                        <Button type="button" data-testid="generate-more-captions" variant="outline"
                          onClick={() => setCaptionsShown((n) => Math.min((cfg.caption_styles || []).length, n + 3))}
                          className="rounded-full border-white/15 bg-transparent text-slate-200 hover:bg-white/5 hover:text-white">
                          <Sparkles className="h-4 w-4 mr-2" /> Generate more captions
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

          </motion.div>
        </AnimatePresence>
      </div>

      {/* nav */}
      <div className="mt-12 flex items-center justify-between">
        <Button
          data-testid="wizard-back-btn"
          variant="ghost"
          disabled={step === 0}
          onClick={() => setStep((s) => Math.max(0, s - 1))}
          className="text-slate-300 hover:text-white disabled:opacity-30"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back
        </Button>

        {step < stepList.length - 1 ? (
          <Button
            data-testid="wizard-next-btn"
            disabled={!canNext}
            onClick={() => setStep((s) => s + 1)}
            className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-7 py-6 font-semibold transition-colors disabled:opacity-40"
          >
            Continue <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        ) : (
          <Button
            data-testid="generate-btn"
            disabled={submitting}
            onClick={submit}
            className="bg-[#FF453A] hover:bg-[#FF5D54] text-white rounded-full px-8 py-6 text-base font-semibold transition-colors glow-coral disabled:opacity-60"
          >
            {submitting ? <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Starting…</> : <><Sparkles className="mr-2 h-5 w-5" /> Generate my ad</>}
          </Button>
        )}
      </div>
    </div>
  );
}
